import { useEffect, useMemo, useState } from "react";
import { businessDateOf, currentBusinessDate } from "./lib/business-day";
import { api } from "./lib/api";
import type { RestaurantBooking, Site } from "./types";

interface CloudflareData {
  configured: boolean;
  analyticsStatus?: string;
  traffic: {
    totals: { requests: number; pageViews: number; dailyUniquesSum: number };
    days: { dimensions: { date: string }; sum: { pageViews: number; requests: number }; uniq: { uniques: number } }[];
  } | null;
}
interface GoogleMetrics {
  totals: { clicks: number; impressions: number; ctr: number; position: number };
}
interface GoogleStatus { connected: boolean; }
interface FirstPartyAnalytics {
  totals: { pageViews: number; visitors: number };
  days: { date: string; pageViews: number; visitors: number }[];
}
const integer = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });

function Stat({ label, value, unavailable, onClick }: { label: string; value?: number; unavailable?: string; onClick: () => void }) {
  return <button className="home-stat" onClick={onClick}><small>{label}</small><strong>{typeof value === "number" ? integer.format(value) : "—"}</strong><span>{typeof value === "number" ? "Ultimi 30 giorni" : unavailable || "Dati non disponibili"}</span><i>Apri il dettaglio →</i></button>;
}

export default function SiteHome({ site, userName, canEdit, onNavigate, onOpenBooking }: { site: Site; userName: string; canEdit: boolean; onNavigate: (section: "analytics" | "pages" | "menu" | "bookings" | "marketing" | "seo" | "settings" | "escape" | "rental" | "social") => void; onOpenBooking: (date: string) => void }) {
  const [cloudflare, setCloudflare] = useState<CloudflareData | null>(null);
  const [google, setGoogle] = useState<GoogleMetrics | null>(null);
  const [analytics, setAnalytics] = useState<FirstPartyAnalytics | null>(null);
  const [pendingBookings, setPendingBookings] = useState<RestaurantBooking[] | null>(null);
  const [bookingBusy, setBookingBusy] = useState("");
  const [bookingNotice, setBookingNotice] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [loading, setLoading] = useState(true);
  const restaurantWorkspace = site.modules?.includes("restaurant_bookings") || false;
  useEffect(() => {
    const googleMetrics = api<GoogleStatus>(`/api/sites/${site.id}/integrations/google`)
      .then((status) => status.connected
        ? api<GoogleMetrics>(`/api/sites/${site.id}/integrations/google/metrics`)
        : null);
    Promise.allSettled([
      api<FirstPartyAnalytics>(`/api/sites/${site.id}/analytics`),
      api<CloudflareData>(`/api/sites/${site.id}/integrations/cloudflare`),
      googleMetrics,
    ]).then(([analyticsResult, cloudflareResult, googleResult]) => {
      if (analyticsResult.status === "fulfilled") setAnalytics(analyticsResult.value);
      if (cloudflareResult.status === "fulfilled") setCloudflare(cloudflareResult.value);
      if (googleResult.status === "fulfilled" && googleResult.value) setGoogle(googleResult.value);
      setLoading(false);
    });
  }, [site.id]);
  useEffect(() => {
    if (!restaurantWorkspace) return;
    let active = true;
    const loadPending = () => api<{ bookings: RestaurantBooking[] }>(`/api/sites/${site.id}/restaurant-bookings?status=pending`)
      .then((result) => { if (active) setPendingBookings(result.bookings); })
      .catch(() => { if (active) setPendingBookings([]); });
    const refreshWhenVisible = () => { if (document.visibilityState === "visible") void loadPending(); };
    void loadPending();
    const timer = window.setInterval(loadPending, 30000);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [restaurantWorkspace, site.id]);
  const visiblePendingBookings = useMemo(() => {
    if (!pendingBookings) return pendingBookings;
    const today = currentBusinessDate();
    return pendingBookings.filter((booking) => businessDateOf(booking.date, booking.time) >= today);
  }, [pendingBookings]);
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    return hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";
  }, []);
  const chart = analytics?.days.length
    ? analytics.days.map((day) => ({ date: day.date, pageViews: day.pageViews }))
    : (cloudflare?.traffic?.days || []).map((day) => ({ date: day.dimensions.date, pageViews: day.sum.pageViews }));
  const max = Math.max(1, ...chart.map((day) => day.pageViews));
  const analyticsHint = cloudflare?.configured ? "Analytics non disponibili per questo dominio" : "Collega Cloudflare Analytics";
  async function confirmBooking(booking: RestaurantBooking) {
    if (!canEdit || bookingBusy) return;
    setBookingBusy(booking.id);
    setBookingNotice("");
    setBookingError("");
    try {
      await api(`/api/sites/${site.id}/restaurant-bookings/${booking.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "confirmed" }),
      });
      setPendingBookings((current) => current?.filter((item) => item.id !== booking.id) || []);
      setBookingNotice(`Prenotazione ${booking.reference} confermata correttamente.`);
    } catch (cause) {
      setBookingError(cause instanceof Error ? cause.message : "Impossibile confermare la prenotazione");
    } finally {
      setBookingBusy("");
    }
  }
  return <div className="site-home">
    <header className="site-home-welcome">
      <div><p className="eyebrow indigo">Panoramica sito</p><h2>{greeting}, {userName.split(" ")[0]}</h2><p>Qui trovi attività, visite e stato del sito {site.name}.</p></div>
      <a className="secondary" href={site.domain ? `https://${site.domain}` : `https://${site.slug}.pages.dev`} target="_blank" rel="noreferrer">Apri il sito ↗</a>
    </header>
    <section className="site-summary-strip">
      <div className="summary-site-mark">{site.name.slice(0, 1).toUpperCase()}</div>
      <div><strong>{site.name}</strong><span className={`status ${site.status}`}>{site.status === "published" ? "Online" : "Bozza"}</span><small>{site.domain || `${site.slug}.pages.dev`}</small></div>
      <button onClick={() => onNavigate("settings")}>Impostazioni del sito</button>
    </section>
    {restaurantWorkspace && <section className={`home-pending${visiblePendingBookings?.length ? " has-pending" : ""}`}>
      <div className="home-section-head"><div><p className="eyebrow indigo">Da gestire</p><h3>Prenotazioni da confermare</h3><p>Richieste aperte da oggi in poi. Le serate già chiuse (dopo le 3:00) non compaiono più qui.</p></div><strong className="home-pending-count">{visiblePendingBookings?.length ?? "…"}</strong></div>
      {bookingNotice && <div className="notice success">{bookingNotice}</div>}
      {bookingError && <div className="notice error">{bookingError}</div>}
      {visiblePendingBookings === null ? <div className="home-pending-loading">Controllo delle richieste in corso…</div> : visiblePendingBookings.length === 0 ? <div className="home-pending-empty"><strong>Tutto gestito</strong><span>Non ci sono prenotazioni in attesa di conferma.</span></div> : <div className="home-pending-list">
        {visiblePendingBookings.map((booking) => <article key={booking.id}>
          <time dateTime={`${booking.date}T${booking.time}`}><strong>{new Date(`${booking.date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</strong><span>{booking.time}</span></time>
          <div><strong>{booking.customer.firstName} {booking.customer.lastName}</strong><span>{booking.partySize} persone · {booking.customer.phone}</span><small>Richiesta inviata il {new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(booking.createdAt))}</small><small>Codice {booking.reference}</small></div>
          <div className="home-pending-actions"><button className="secondary" onClick={() => onOpenBooking(booking.date)}>Dettagli</button><button className="primary" disabled={!canEdit || Boolean(bookingBusy)} onClick={() => confirmBooking(booking)}>{bookingBusy === booking.id ? "Confermo…" : "Conferma"}</button></div>
        </article>)}
      </div>}
    </section>}
    <section className="home-analytics">
      <div className="home-section-head"><div><h3>Statistiche</h3><p>Le metriche principali degli ultimi 30 giorni, lette dalle fonti collegate.</p></div><button onClick={() => onNavigate("seo")}>SEO e GEO</button></div>
      {loading ? <div className="home-stat-skeleton"><i /><i /><i /><i /></div> : <div className="home-stats-grid">
        <Stat label="Visualizzazioni del sito" value={analytics?.totals.pageViews ?? cloudflare?.traffic?.totals.pageViews} unavailable={analyticsHint} onClick={() => onNavigate("analytics")} />
        <Stat label="Visitatori" value={analytics?.totals.visitors ?? cloudflare?.traffic?.totals.dailyUniquesSum} unavailable={analyticsHint} onClick={() => onNavigate("analytics")} />
        <Stat label="Clic da Google" value={google?.totals.clicks} unavailable="Collega Search Console" onClick={() => onNavigate("analytics")} />
        <Stat label="Impressioni Google" value={google?.totals.impressions} unavailable="Collega Search Console" onClick={() => onNavigate("analytics")} />
      </div>}
      <div className="home-chart-wrap">
        <div><strong>Andamento visualizzazioni</strong><small>{chart.length ? `${chart.length} giorni con dati` : "Il grafico apparirà quando saranno disponibili le prime visite"}</small></div>
        <div className="home-chart" aria-label="Visualizzazioni giornaliere">
          {chart.map((day) => <i key={day.date} style={{ height: `${Math.max(4, day.pageViews / max * 100)}%` }} title={`${day.date}: ${day.pageViews}`} />)}
          {!chart.length && Array.from({ length: 30 }, (_, index) => <i className="empty" key={index} style={{ height: `${8 + (index % 5) * 2}%` }} />)}
        </div>
      </div>
    </section>
    <section className="home-actions">
      <div className="home-section-head"><div><h3>Gestisci il sito</h3><p>Le attività più frequenti sono sempre a portata di mano.</p></div></div>
      <div className="home-action-grid">
        {[
          { id: "pages", title: "Modifica il sito", text: "Aggiorna testi, foto e pagine", show: true },
          { id: "rental", title: "Prenotazioni case", text: "Richieste degli ospiti, calendario e chiusure di tutte le case", show: site.modules?.includes("short_rental") },
          { id: "escape", title: "Prenotazioni ed eventi", text: "Agenda, turni, schede evento e foto", show: site.modules?.includes("escape_bookings") },
          { id: "menu", title: "Menu ristorante", text: "Piatti, drink, prezzi e varianti", show: site.modules?.includes("restaurant_menu") },
          { id: "bookings", title: "Prenotazioni", text: "Agenda, coperti e mappa tavoli", show: restaurantWorkspace && !site.modules?.includes("escape_bookings") },
          { id: "social", title: "Lavori e Instagram", text: "Galleria dei lavori e post social", show: site.modules?.includes("social_gallery") },
          { id: "seo", title: "SEO e GEO", text: "Titoli, descrizioni e visibilità su Google", show: !site.modules?.includes("restaurant_menu") },
        ].filter((action) => action.show).slice(0, 3).map((action, index) => (
          <button key={action.id} onClick={() => onNavigate(action.id as Parameters<typeof onNavigate>[0])}><span>{String(index + 1).padStart(2, "0")}</span><strong>{action.title}</strong><small>{action.text}</small></button>
        ))}
        <button onClick={() => onNavigate("marketing")}><span>04</span><strong>Marketing e clienti</strong><small>CRM, consensi e segmenti</small></button>
        <button onClick={() => onNavigate("seo")}><span>05</span><strong>SEO e GEO</strong><small>Indicizzazione e presenza su Google</small></button>
      </div>
    </section>
  </div>;
}
